import React, { useContext, useState } from 'react';
import { Button, Text } from '@chakra-ui/react';
import { colors, media } from '../../../../../App/ChakraTheme';

import Loader from '../../../../../components/Loader';
import { useGetStudentTeams } from '../../../hooks/useGetStudentTeams';
import StudentTeam from './StudentTeam';
import styled from '../../../../../consts/styled';
import useFileServerStudentTeams from './hooks/useFileServerStudentTeams';
import groupProject from './api/groupProject';
import redirectToChatbotEditor from './helpers/redirectToChatbotEditor';
import { AuthContext } from '../../../../../context/AuthProvider';
import {
  Program,
  useSchoolForStudyGroupQuery,
  StudyGroupQuery,
} from '../../../../../generated/graphqlModels';
import createEditorUser from './helpers/createEditorUser';
import { AddIcon } from '@chakra-ui/icons';
import { SECONDARY_FONT_FAMILY } from '../../../../../App/ChakraTheme';
import { useTranslation } from 'react-i18next';

interface StudentTeamsProps {
  studyGroupPk: string;
  students: StudyGroupQuery['studyGroup']['students'];
  program: Program;
}

const StudentTeams = (props: StudentTeamsProps) => {
  const { studyGroupPk, students, program } = props;
  // TODO: not good practice
  const user = useContext(AuthContext);
  const isIot = program.name.toLocaleLowerCase().includes('iot');
  const isProgramUsingFileServer = program.name.includes('AI') || isIot;
  const { t } = useTranslation();
  const { data: schoolData } = useSchoolForStudyGroupQuery({
    variables: {
      studyGroupId: studyGroupPk,
    },
  });

  const [isAddingMissingStudents, setIsAddingMissingStudents] = useState(false);
  const [isAllLoading, setIsAllLoading] = useState(false);
  const [
    studentTeams,
    {
      counter,
      isLoading,
      isSaving,
      isChanged,
      isTeamAdded,
      areAllStudentTeamsValid,
      deleteTeam,
      save,
      setStudentTeams,
      setIsChanged,
    },
  ] = useGetStudentTeams(studyGroupPk, students);
  const programFileServerNamespace = isIot ? 'EMBED_PYTHON' : 'CHATBOT';
  const {
    groupTemplates,
    missingStudents,
    fileServerStudents,
    projects,
    token,
    isLoading: isFileServerDataLoading,
    mainGroupTemplateId,
    setAnswersId,
    getInitialData,
  } = useFileServerStudentTeams(
    students,
    isProgramUsingFileServer,
    programFileServerNamespace,
  );

  const studentTeamConnectedProjects = isProgramUsingFileServer
    ? studentTeams.map((studentTeam) => {
        const memberIds = (studentTeam.oldMembers || []).map((m) => m.pk);
        const studentTeamFileServerStudents = fileServerStudents.filter((s) =>
          memberIds.includes(s.platformId),
        );
        // Find project using ids
        const userIds = studentTeamFileServerStudents
          .map((m) => m.platformId)
          .sort((a, b) => (a! > b! ? 1 : -1))
          .join(',');
        let fileServerProject = projects.find((p) => {
          const projectUserIds = p.collaboratorIds
            .sort((a, b) => (a > b ? 1 : -1))
            .join(',');
          return projectUserIds === userIds;
        });
        // Find project using name
        if (!fileServerProject) {
          fileServerProject = projects.find(
            (p) => p.name === studentTeam.oldName!,
          );
        }
        return fileServerProject;
      })
    : [];

  const saveAll = async () => {
    if (!areAllStudentTeamsValid) {
      alert('Student team is not valid');
      return false;
    }
    setIsAllLoading(true);
    try {
      await save();
      await Promise.all(
        studentTeams.map(async (studentTeam, index) => {
          const studentTeamConnectedProject =
            studentTeamConnectedProjects[index];
          if (studentTeamConnectedProject) {
            const memberIds = studentTeam.members.map((m) => m.pk);
            const studentTeamFileServerStudents = fileServerStudents.filter(
              (s) => memberIds.includes(s.platformId),
            );
            await groupProject.updateGroupProject({
              ...studentTeamConnectedProject,
              id: studentTeamConnectedProject.id,
              name: studentTeam.name,
              collaboratorIds: studentTeamFileServerStudents.map((s) => s.id!),
              type: isIot
                ? 'PYTHON'
                : studentTeamConnectedProject.attributes.answers_id
                ? 'ANSWERS'
                : 'RASA',
            });
          }
        }),
      );
      await getInitialData();
    } catch (e) {
      console.log(e);
      setIsAllLoading(false);
      return false;
    }
    setIsAllLoading(false);
    return true;
  };

  const addMissingStudentsToEditor = async () => {
    setIsAddingMissingStudents(true);
    let hasError: boolean = false;
    try {
      const results = await Promise.allSettled(
        missingStudents.map((student) =>
          createEditorUser(studyGroupPk, student),
        ),
      );
      hasError = results.reduce(
        (acc, cur: any) => acc || !cur.value.id,
        false,
      ) as any;
    } catch (e) {
      console.log(e);
      hasError = true;
    }
    if (hasError) {
      alert('Something went wrong');
    }
    setIsAddingMissingStudents(false);
  };

  return (
    <div>
      {isProgramUsingFileServer && missingStudents.length ? (
        isAddingMissingStudents ? (
          <Loader />
        ) : (
          <div>
            <Text as="h5" textStyle="h5" mb={3}>
              {t('class.studentTeams.notAllStudentsUsingEditor')}
            </Text>
            <Button colorScheme="blue" onClick={addMissingStudentsToEditor}>
              {t('class.studentTeams.createMissingStudents')}
            </Button>
          </div>
        )
      ) : null}
      <StudentTeamsContainer>
        {(isLoading || isFileServerDataLoading) && <Loader />}
        <table>
          <thead>
            <tr>
              <th>{t('class.studentTeams.name')}</th>
              <th>{t('class.studentTeams.members')}</th>
              <th>{t('class.studentTeams.teamLeader')}</th>
              {isProgramUsingFileServer && (
                <>
                  <th>{t('class.studentTeams.type')}</th>
                  <th>{t('class.studentTeams.open')}</th>
                </>
              )}
              <th>
                <th>{t('class.studentTeams.edit')}</th>
              </th>
            </tr>
          </thead>
          <tbody key={counter}>
            {studentTeams.map((studentTeam, index) => {
              const freeStudents = [...students];
              studentTeams.forEach((studentTeam) => {
                studentTeam.members.forEach((member) => {
                  const memberIndex = freeStudents.findIndex(
                    (s) => s.pk === member.pk,
                  );
                  if (memberIndex !== -1) {
                    freeStudents.splice(memberIndex, 1);
                  }
                });
              });
              const allowedStudents = [...studentTeam.members, ...freeStudents];
              const hasMissingStudent = studentTeam.members.reduce(
                (acc, cur) => {
                  if (acc) {
                    return acc;
                  }
                  return !!missingStudents.find(
                    (missingStudent) => missingStudent.pk === cur.pk,
                  );
                },
                false,
              );
              const memberIds = studentTeam.members.map((m) => m.pk);
              const studentTeamFileServerStudents = fileServerStudents.filter(
                (s) => memberIds.includes(s.platformId),
              );

              const fileServerProject = studentTeamConnectedProjects[index];

              return (
                <StudentTeam
                  key={studentTeam.pk || index}
                  isProgramUsingFileServer={isProgramUsingFileServer}
                  hasMissingStudent={hasMissingStudent}
                  fileServerStudents={studentTeamFileServerStudents}
                  groupTemplates={groupTemplates}
                  studentTeam={studentTeam}
                  students={allowedStudents}
                  fileServerProject={fileServerProject}
                  onNameChange={(name: string) => {
                    studentTeams[index]!.name = name;
                    setStudentTeams([...studentTeams]);
                  }}
                  answersId={fileServerProject?.attributes.answers_id || ''}
                  setAnswersId={(answersId: string) => {
                    if (fileServerProject) {
                      setAnswersId(fileServerProject.id, answersId);
                      setIsChanged(true);
                    }
                  }}
                  onTeamLeaderChange={(option: any) => {
                    studentTeams[index]!.teamLeader = students.find(
                      (s) => s.pk === option.value,
                    )!;
                    setStudentTeams([...studentTeams]);
                  }}
                  onMemberChange={(
                    options: { value: string; label: string }[],
                  ) => {
                    studentTeams[index]!.members = options
                      ? options.map(
                          (option: any) =>
                            students.find((s) => s.pk === option.value)!,
                        )
                      : [];
                    setStudentTeams([...studentTeams]);
                  }}
                  deleteTeam={() => {
                    deleteTeam(index);
                  }}
                  save={saveAll}
                  createGroupProject={async () => {
                    if (!fileServerProject && studentTeams[index]) {
                      await groupProject.createGroupProject({
                        name: studentTeam.name,
                        collaboratorIds: studentTeamFileServerStudents.map(
                          (m) => m.id as string,
                        ),
                        templateId: mainGroupTemplateId,
                        schoolId: schoolData?.studyGroup.license.school.id,
                        schoolName: schoolData?.studyGroup.license.school.name,
                      });
                      await getInitialData();
                    }
                  }}
                  redirectToChatbotEditor={(
                    id: string,
                    name: string,
                    type: string,
                  ) => {
                    return redirectToChatbotEditor({
                      studentProjectId: id,
                      platformId: user!.pk,
                      text: `You are viewing ${name} group project`,
                      sessionId: user!.session_id,
                      token: user!.token,
                      onlyInfo: type === 'ANSWERS',
                    });
                  }}
                  toggleProjectIsShown={async () => {
                    if (fileServerProject) {
                      await groupProject.updateIsShown(
                        fileServerProject.id,
                        !fileServerProject.isShown,
                      );
                      await getInitialData();
                    }
                  }}
                />
              );
            })}
          </tbody>
        </table>
        {studentTeams.length === 0 && (
          <div>{t('class.studentTeams.noStudentTeams')}</div>
        )}
        {!isTeamAdded && (
          <Button
            type="button"
            colorScheme="blue"
            m={5}
            onClick={() => {
              studentTeams.push({
                pk: null,
                studyGroup: studyGroupPk,
                name: `Team ${studentTeams.length + 1}`,
                members: [],
                isEditMode: true,
              });
              setStudentTeams([...studentTeams]);
            }}
            leftIcon={<AddIcon />}
          >
            {t('button.add')}
          </Button>
        )}
      </StudentTeamsContainer>
    </div>
  );
};

export default StudentTeams;

const StudentTeamsContainer = styled.div`
  width: 100%;
  justify-content: center;
  align-items: center;
  text-align: center;

  > table {
    border-collapse: collapse;
    margin: 25px 0;
    font-size: 0.9em;
    font-family: sans-serif;
    min-width: 400px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
    font-family: ${SECONDARY_FONT_FAMILY}
  }

  > table thead tr {
    background-color: ${colors.darkblue};
    color: #ffffff;
    text-align: left;
  }

  > table th,
  > table td {
      padding: 12px 15px;
      min-height: 100px;
      max-height: 100px;
      text-align: center;
  }

  > table td {
    min-width: 100px;
  }

  > table tbody tr {
    border-bottom: 1px solid #dddddd;
    height: 100px;
  }

  > table tbody tr:nth-of-type(even) {
      background-color: #DDD;
  }

  > table tbody tr:last-of-type {
      border-bottom: 2px solid #3191d6;
  }

  > .row-cs {
    margin-right: -8px;
    margin-left: -8px;
    flex-wrap: wrap;
    align-content: stretch;
    width: 100%;
    justify-content: flex-start;
    ${media.tablet`
    justify-content: center;
  `}
    > .col-cs {
      padding-right: 8px;
      padding-left: 8px;
      width: 50%;
      flex: 0 0 50%;
      ${media.tabletLandscape`
      flex-basis: 50%;
    `}
      ${media.tablet`
      flex-basis: 100%;
      width: 100%;
    `}${media.phone`
      flex-basis: 100%;
      width: 100%;
    `}
    }
`;
